import * as L from "leaflet";
import { Station, BadgeKind, DataSampleType, DataSample, StationStatus } from "../../domain/types";
import { normalizeDirection } from "../../domain/utils/wind";
import { getColorByValueAndType, normalizeBySampleType } from "../../domain/utils/colors";
import { getLastUpdatedSampleTimestamp, getLatestSample } from "../data/latestData";
import { BADGE_ICONS } from "../../domain/visuals/icons";
import { formatData } from "../data/formattingData";
import { centerOffsetForPath } from "../../domain/utils/index";
import { getStatusManager } from "../state/statusLayers";

declare const zoomScale: number; // provided by main/init

export function getStationStatus(lastUpdatedMs: number): StationStatus {
	const updateIntervalMs = 10 * 60 * 1000; // 10 min: 10 * 60 * 1000
	const age = Date.now() - lastUpdatedMs;
	if (age <= updateIntervalMs * 1.5) return 'fresh';
	if (age <= updateIntervalMs * 6) return 'stale';
	return 'offline';
}

export function getBadges(s: Station, sizeCircle = 40, getIconsOnly = false) {
	let badges: BadgeKind[] = [];
	if (s.HasWindData) badges.push("wind");
	if (s.HasCurrentData) badges.push("current");
	if (s.HasWaveData) badges.push("wave");
	if (s.HasAirTemperatur) badges.push("temp");
	if (s.HasStillImages) badges.push("camera");
	if (s.HasLiveFeed) badges.push("video");

	const circleScaled = sizeCircle * zoomScale;
	const badgeSize = 25 * zoomScale;
	const badgeGapDegrees = 60;
	const offset = -30;

	const cx = circleScaled / 2;
	const cy = offset;
	const half = (badges.length - 1) / 2;
	const angles: number[] = badges.map((_, i) => 90 + (i - half) * badgeGapDegrees);
	const badgeHtml = badges.map((kind, i) => {
		const rad = (angles[i] * Math.PI) / 180;
		const x = cx + (circleScaled / 2 + badgeSize / 2) * Math.cos(rad);
		const y = cy - (circleScaled / 2 + badgeSize / 2) * Math.sin(rad);
		if (getIconsOnly) {
			return `<i class="${BADGE_ICONS[kind]}" style="font-size:${Math.round(badgeSize * 0.6)}px;"></i>`;
		} else {
			return `<div class="badge"
			   style="left:${x}px; top:${-y}px; width:${badgeSize}px; height:${badgeSize}px;">
			<i class="${BADGE_ICONS[kind]}" style="font-size:${Math.round(badgeSize * 0.6)}px;"></i>
		  </div>`;
		}
		
	}).join("");
	return { html: badgeHtml, badgeSize, offset };
}

export function circleIcon(s: Station, showText = true, sizeCircle = 40, sizeIcon = 90): L.DivIcon {
	const status = getStationStatus(getLastUpdatedSampleTimestamp(s).getTime());
	getStatusManager().update(s.Id, status);
	const data = getDataIcon(s);
	const dataIcon = data.svg;

	const circleScaled = sizeCircle * zoomScale;
	const iconScaled = sizeIcon * zoomScale;

	const speedGap = 6 * zoomScale;
	const outerSize = circleScaled + speedGap * 2;
	const ocx = outerSize / 2, ocy = outerSize / 2;
	const or = (outerSize / 2) - 1.5;
	const ocirc = 2 * Math.PI * or;

	const anim = getAnimationContext(s);

	const initialOffset = (1 - anim.progress) * ocirc;
	const speedColor = (status === 'offline')
		? 'rgba(189,189,189,0.5)'   // muted gray for offline
		: anim.color;

	const speedometerHtml = anim.hasAnim ? `
  <svg class="speedometer"
       width="${outerSize}" height="${outerSize}"
       viewBox="0 0 ${outerSize} ${outerSize}"
       style="position:absolute; left:${-speedGap}px; top:${-speedGap}px; pointer-events:none;">
    <circle cx="${ocx}" cy="${ocy}" r="${or}"
            fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="3" />
    <circle class="speedo-fg"
            cx="${ocx}" cy="${ocy}" r="${or}"
            fill="none" stroke="${speedColor}" stroke-width="3" stroke-linecap="round"
            data-circumference="${ocirc}"
            style="stroke-dasharray:${ocirc} ${ocirc}; stroke-dashoffset:${initialOffset}"
            transform="rotate(-90 ${ocx} ${ocy})" />
  </svg>` : '';


	const badges = getBadges(s, sizeCircle, false);
	const badgeHtml = badges.html;
	const badgeSize = badges.badgeSize;
	const offset = badges.offset;

	const bgColor = "#263238";
	const fColor = "#CFD8DC";

	const filters =
		status !== 'offline' ? 'filter: grayscale(60%) brightness(0.9) saturate(0.75);' :
			'filter: grayscale(100%) brightness(0.65); opacity:0.6;';
	const ringStroke =
		status === 'fresh' ? '' :
			status === 'stale' ? `stroke-dasharray="4 3" stroke="${fColor}" stroke-width="2" fill="none"` :
				`stroke="#9E9E9E" stroke-width="2.5" fill="none"`;

	const overlayOffline = status === 'offline'
		? `<line x1="8" y1="8" x2="${circleScaled - 8}" y2="${circleScaled - 8}" stroke="#BDBDBD" stroke-width="2"/>
       <line x1="${circleScaled - 8}" y1="8" x2="8" y2="${circleScaled - 8}" stroke="#BDBDBD" stroke-width="2"/>`
		: '';

	const textHtml = showText
		? `<div class="marker-text ${status !== 'fresh' ? 'is-dim' : ''} ${status}"
           style="top:${circleScaled + badgeSize - (15)}px;background:${bgColor};color:${fColor}; font-size: ${zoomScale * 150};">
         ${formatData(s, true)}
       </div>`
		: "";
	const stationInfo = showText ? `<div class="marker-text"
           style="top:${-(circleScaled + badgeSize + (1.5 * offset)) - (10 / zoomScale)}px;background:${bgColor};color:${fColor}; font-size: ${zoomScale * 100}%">
         ${s.Name}
       </div>` : "";

	const markerBg = sizeCircle === 40 ? data.markerBgColor : anim.color;
	const markerFg = data.markerFgColor;
	const html = `
   <div class="circle-icon circle-icon--${status}"
     data-station-id="${s.Id}"
     data-anim-value="${anim.value}"
     data-anim-type="${anim.type}"
	 data-anim-enabled="${anim.hasAnim}"
     data-prev-progress="-1"
     style="position:relative; overflow:visible;">
      ${speedometerHtml}
      <div class="marker ${status}"
           style="width:${circleScaled}px; height:${circleScaled}px; ${filters}">
        <div class="marker-bg" style="background:${markerBg};color:${markerFg}"></div>

        <svg class="ring" width="${circleScaled}" height="${circleScaled}" viewBox="0 0 ${circleScaled} ${circleScaled}">

          ${ringStroke
			? `<circle cx="${circleScaled / 2}" cy="${circleScaled / 2}" r="${(circleScaled / 2) - 4}" ${ringStroke}/>`
			: ''
		}
          ${overlayOffline}
        </svg>

        <div class="icon-wrap" style="width:${iconScaled}px; height:${iconScaled}px;">
          ${status !== "offline" ? dataIcon : ""}
        </div>
      </div>
      <div class="badge-arc hidden-badges">
        ${showText ? "" : badgeHtml}
      </div>

		${stationInfo}
      ${textHtml}
    </div>
  `;

	const totalHeight = sizeCircle + badgeSize * 2 + offset * 2;

	return L.divIcon({
		className: "", // keep Leaflet from adding defaults
		html,
		iconSize: [sizeCircle, totalHeight],
		iconAnchor: [sizeCircle / 2, sizeCircle / 2],
	});
}

export function animateSpeedometerOutside(sample: DataSample, duration = 600) {
	if (!hasAnimation(sample.Type)) {
		return;
	}
	const root = document.querySelector<HTMLElement>(`.circle-icon[data-station-id="${sample.StationId}"]`);
	if (!root) return;

	// if the marker is offline, don’t animate (optional)
	if (root.classList.contains('circle-icon--offline')) return;

	const arc = root.querySelector<SVGCircleElement>('.speedometer .speedo-fg');
	if (!arc) return;


	if (root.dataset.animType !== String(sample.Type)) {
		return; // Do not update, you are not the boss!
	}
	const circ = parseFloat(arc.getAttribute('data-circumference') || '0');

	const nextP = normalizeBySampleType(sample.Value, sample.Type);
	const prevP = parseFloat(root.dataset.prevProgress ?? '0');

	arc.setAttribute('stroke', getColorByValueAndType(sample.Value, sample.Type));

	const start = performance.now();
	function frame(now: number) {
		if (!arc) {
			return;
		}
		const t = Math.min(1, (now - start) / duration);
		const p = prevP + (nextP - prevP) * (1 - Math.pow(1 - t, 3)); // easeOutCubic inline
		arc.style.strokeDashoffset = String((1 - p) * circ);
		if (t < 1) requestAnimationFrame(frame);
	}
	requestAnimationFrame(frame);

	root.dataset.prevProgress = String(nextP);
	root.dataset.animValue = String(sample.Value);
	root.dataset.animType = String(sample.Type);
}

type AnimCtx = {
	hasAnim: boolean;
	value: number;
	type: DataSampleType;
	progress: number;
	color: string;
};
function hasAnimation(type: DataSampleType): boolean {
	return type === DataSampleType.NONE || type === DataSampleType.CURRENT_DIRECTION || type === DataSampleType.WIND_DIRECTION;
}
export function getAnimationContext(s: Station): AnimCtx {
	const { animationSampleType } = getDataIcon(s);
	if (hasAnimation(animationSampleType)) {
		return { hasAnim: false, value: 0, type: DataSampleType.NONE, progress: 0, color: "transparent" };
	}
	const sample = getLatestSample(s, animationSampleType);

	const value = (sample && (sample as any).Valid !== false) ? sample.Value : -1;
	const type = animationSampleType;
	const progress = normalizeBySampleType(value, type);
	const color = getColorByValueAndType(value, type);

	return { hasAnim: true, value, type, progress, color };
}

const PALETTE = {
	wind: { base: "#0077ff", ring: "#2b9dff" },
	current: { base: "#00a3a3", ring: "#2fcaca" },
	temp: { base: "#ff7a00", ring: "#ff9a3d" },
	textDark: "#111",
	textLight: "#fff",
	halo: "rgba(255,255,255,0.9)",
	ringBg: "rgba(0,0,0,0.15)"
} as const;

export const unknownData =
	"M125,118c-3.3,0-6,2.7-6,6h2c0-2.2,1.8-4,4-4s4,1.8,4,4" +
	"c0,1.4-0.7,2.5-1.9,3.4-0.6,0.4-1.2,0.8-1.8,1.2" +
	"-1.2,0.8-2.3,1.8-2.3,3.3v1.7h2v-1.4c0-0.6,0.3-1,0.8-1.3" +
	"c1.1-0.7,2.1-1.4,2.9-2.1c1.7-1.3,2.3-2.8,2.3-4.8" +
	"C131,120.7,128.3,118,125,118z " +
	"M125,134a1.8,1.8 0 1 0 0,3.6a1.8,1.8 0 1 0 0-3.6z";

const windArrow = "M125,112V76 M125,125l7-12.1h-14L125,125z";

const currentArrowFilledD = "M118,132 L125,118 L132,132";

type iconType = "none" | "windSpeed&dir" | "gust" | "currentSpeed" | "currentSpeed&dir" | "airtemp" | "camera" | "video";
type dataIconType = { svg: string, markerBgColor: string, markerFgColor: string, stroke: string, animationSampleType: DataSampleType, key: iconType };
export function getDataIcon(s: Station): dataIconType {
	let fill = "#263238";
	let stroke = "#CFD8DC"
	const viewBox = 250;
	let strokeWidth = 7 * zoomScale;
	let result:dataIconType = {
		//svg: `'<path class="svg-wb" transform="translate(125,125) scale(1.5)  translate(-125 -125)" d="${unknownData}" />'`,
		svg: `<i class="fa-solid fa-question" style="font-size:${zoomScale * 200}%; color:#CFD8DC;position: absolute;left:30px;top:33px;"></i>`,
		markerBgColor: "#263238",
		markerFgColor: "#CFD8DC",
		stroke: stroke,
		animationSampleType: DataSampleType.NONE,
		key: "none" as iconType
	};
	
	if (s.HasWindData) {
		fill = stroke;
		const direction = getLatestSample(s, DataSampleType.WIND_DIRECTION);
		const speed = getLatestSample(s, DataSampleType.WIND_SPEED);
		if (speed && direction) {
			result.svg = `<path class="svg-wb" d="${windArrow}" />`;
			const { dx, dy } = centerOffsetForPath(result.svg)
			result.svg = `<g transform="translate(${dx}, ${dy}) rotate(${direction.Value}, ${(viewBox / 2) - dx}, ${(viewBox / 2) - dy})">${result.svg}</g>`;
		}
		result.animationSampleType = DataSampleType.WIND_SPEED;
		result.key = "windSpeed&dir";

	} else if (s.HasCurrentData) {
		strokeWidth = 3 * zoomScale;
		const direction = getLatestSample(s, DataSampleType.CURRENT_DIRECTION);
		const speed = getLatestSample(s, DataSampleType.CURRENT_SPEED);
		result.animationSampleType = DataSampleType.CURRENT_SPEED;
		if (direction && direction.Valid) {
			result.svg = `<path class="svg-wb" transform="translate(125,125) scale(2.5) translate(-125 -125)" d="${currentArrowFilledD}" />`;
			result.svg = `<g transform="translate(0,0) rotate(${normalizeDirection(direction.Value)}, ${(viewBox / 2)}, ${(viewBox / 2)})">${result.svg}</g>`;
			result.key = "currentSpeed&dir";
		}
		else if (speed) {
			result.svg = `<i class="${BADGE_ICONS["current"]}"style="font-size:${zoomScale * 200}%; color:#CFD8DC"></i>`
			result.key = "currentSpeed";
			return result;
		}
	} else if (s.HasAirTemperatur) {
		result.svg = `<i class="${BADGE_ICONS["temp"]}"style="font-size:${zoomScale * 200}%; color:#CFD8DC"></i>`
		result.key = "airtemp";
		return result;
	} else if (s.HasLiveFeed) {
		result.svg = `<i class="${BADGE_ICONS["video"]}"style="font-size:${zoomScale * 200}%; color:#CFD8DC"></i>`
		result.key = "video";
		return result;
	} else if (s.HasStillImages) {
		result.svg = `<i class="${BADGE_ICONS["camera"]}"style="font-size:${zoomScale * 200}%; color:#CFD8DC"></i>`
		result.key = "camera";
		return result;
	}
	else {
		strokeWidth = 3;
	}
	result.svg = `<div style="position:relative; display:inline-block;">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 250 250" style="fill:${fill};
						stroke:${stroke};
						stroke-width:${strokeWidth};
						stroke-linecap:round;
						stroke-linejoin:round;
						stroke-miterlimit:1;">
					${result.svg}
				</svg>
</div>`;
	return result;
}